import React, {createContext, useContext, useEffect, useState} from 'react';

import fetcher from "../fetcher";
import {useFetchHook} from "../hooks/useFetchHook";
import useDidMountEffect from "../hooks/useDidMountEffect";
import {useDebounce} from "@uidotdev/usehooks";

export const UserContext = createContext(undefined)

export const UserContextProvider = ( { children } ) => {
    const [fetchProfileResponse, fetchProfile] = useFetchHook(fetcher.getProfile)
    const [updateProfileResponse, updateProfile] = useFetchHook(fetcher.updateProfile)

    const [profileState, setProfileState] = useState(null)
    const debouncedProfile = useDebounce(profileState, 400)

    const setProfileStateWithUpdate = (newState) => {
        setProfileState({...profileState, ...newState, _updateInBackend: true })
    }

    const setProfileDealStateWithoutUpdate = (newState) => {
        setProfileState({...profileState, ...newState, _updateInBackend: false })
    }

    const isFeatureFlagEnabled = (feature_flag_name) => {
        return profileState?.feature_flags?.includes(feature_flag_name)
    }

    const updateProfileFunc = () => {
        const updateObj = structuredClone(profileState)
        delete updateObj._updateInBackend
        return updateProfile(updateObj)
    }

    const refreshData = () => {
        setProfileState(null)
        fetchProfile()
    }

    const isSuperUser = () => {
        return profileState.is_superuser === 'True'
    }

    useEffect(function fetchProfileFromBackend() {
        fetchProfile()
    }, [])

    useEffect(function setFetchedProfile(){
        if (fetchProfileResponse?.data?.data) {
            setProfileDealStateWithoutUpdate(fetchProfileResponse?.data?.data)
        }
    }, [fetchProfileResponse])

    useDidMountEffect(function updateProfileInBackend(){
        if (profileState?._updateInBackend === true) {
            updateProfileFunc()
        }
    }, [debouncedProfile])

    useDidMountEffect(function updateProfileFromBackend(){
        if (updateProfileResponse?.data?.data && !updateProfileResponse.loading) {
            const updateObj = structuredClone(updateProfileResponse?.data?.data)
            delete updateObj._updateInBackend
            setProfileDealStateWithoutUpdate(updateObj)
        }
    }, [updateProfileResponse])

    useEffect(() => {
        return () => {
            setProfileState(null)
        }
    }, [])

    const value = {
        refreshData,
        profileState,
        setProfileState,

        updateProfile,
        updateProfileResponse,

        setProfileStateWithUpdate,
        setProfileDealStateWithoutUpdate,

        isSuperUser,
        isFeatureFlagEnabled,
    }

    if (!profileState) return null;

    return (
        <UserContext.Provider value={value}>
            { children }
        </UserContext.Provider>
    )
}

export const useUserContext = () => {
    const context = useContext(UserContext)

    if (context === undefined) {
        throw new Error('useUserContext must be used within UserContext')
    }
    return context
}